import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {InvoiceStatus, toInvoiceStatus} from "./invoice-status";
import {toTransportCategory, TransportCategory} from "./transport-category";
import {TransportMethod, toTransportMethod} from "./transport-method";
import {TransportLineItemArrayValidator, TransportLineItemEntity} from "./transport-line-item";
import {TicketCodeArrayValidator, TicketCodeEntity} from "./ticket-code";

export const InvoiceValidator = t.intersection([
  t.type({
    id: t.string,
    invoiceNumber: t.string,
    operatorId: t.string,
    vendorId: t.string,
    status: t.string,
  }),
  t.partial({
    totalCost: t.number,
    cost: t.number,
    taxCost: t.number,
    taxRate: t.number,
    taxAccount: t.string,
    parentInvoiceNumber: t.string,
    type: t.string,
    category: t.string,
    method: t.string,
    containerId: t.string,
    containerName: t.string,
    siteId: t.string,
    siteName: t.string,
    operatingAreaId: t.string,
    operatingAreaName: t.string,
    routeId: t.string,
    routeName: t.string,
    wellId: t.string,
    wellName: t.string,
    rigId: t.string,
    rigName: t.string,
    operatorName: t.string,
    vendorName: t.string,
    secondaryVendorId: t.string,
    secondaryVendorName: t.string,
    waterTypeId: t.string,
    waterTypeName: t.string,
    waterTypeCategory: t.string,
    serviceTypeId: t.string,
    serviceTypeName: t.string,
    serviceTypeCategory: t.string,
    wellStageId: t.string,
    wellStageName: t.string,
    wellStageCategory: t.string,
    regionId: t.string,
    rateModelId: t.string,
    rateModelName: t.string,
    authorizedById: t.string,
    authorizedByName: t.string,
    engineerId: t.string,
    engineerName: t.string,
    invoiceAccountLookupId: t.string,
    invoiceAccountLookupName: t.string,
    dueDttm: t.number,
    fromDttm: t.number,
    toDttm: t.number,
    tliFromDttm: t.number,
    tliToDttm: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    //
    lineItemList: TransportLineItemArrayValidator,
    ticketCodeList: TicketCodeArrayValidator,
    coded: t.boolean,
    disputed: t.boolean,
    dbName: t.string,
  }),
]);

export const InvoiceArrayValidator = t.array(InvoiceValidator);

export type Invoice = t.TypeOf<typeof InvoiceValidator>;
export type InvoiceArray = t.TypeOf<typeof InvoiceArrayValidator>;

@Exclude()
export class InvoiceEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  invoiceNumber!: string

  @Expose()
  parentInvoiceNumber?: string

  @Expose()
  totalCost?: number

  @Expose()
  cost?: number

  @Expose()
  taxCost?: number

  @Expose()
  taxRate?: number

  @Expose()
  taxAccount?: string

  @Expose()
  operatorId!: string

  @Expose()
  operatorName?: string

  @Expose()
  vendorId!: string

  @Expose()
  vendorName?: string

  @Expose()
  secondaryVendorId?: string

  @Expose()
  secondaryVendorName?: string

  @Transform(getDatabaseCodeTransformer(toInvoiceStatus))
  @Expose()
  status!: InvoiceStatus

  @Expose()
  type?: string

  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  @Expose()
  category?: TransportCategory

  @Transform(getDatabaseCodeTransformer(toTransportMethod))
  @Expose()
  method?: TransportMethod

  @Expose()
  containerId?: string

  @Expose()
  containerName?: string

  @Expose()
  siteId?: string

  @Expose()
  siteName?: string

  @Expose()
  operatingAreaId?: string

  @Expose()
  operatingAreaName?: string

  @Expose()
  routeId?: string

  @Expose()
  routeName?: string

  @Expose()
  wellId?: string

  @Expose()
  wellName?: string

  @Expose()
  wellStageId?: string

  @Expose()
  wellStageName?: string

  @Expose()
  wellStageCategory?: string

  @Expose()
  rigId?: string

  @Expose()
  rigName?: string

  @Expose({name: "waterTypeId"})
  freightTypeId?: string;

  @Expose({name: "waterTypeName"})
  freightTypeName?: string;

  @Expose({name: "waterTypeCategory"})
  freightTypeCategory?: string;

  @Expose()
  serviceTypeId?: string;

  @Expose()
  serviceTypeName?: string;

  @Expose()
  serviceTypeCategory?: string;

  @Expose()
  regionId?: string;

  @Expose()
  rateModelId?: string

  @Expose()
  rateModelName?: string

  @Expose()
  authorizedById?: string

  @Expose()
  authorizedByName?: string

  @Expose()
  engineerId?: string

  @Expose()
  engineerName?: string

  @Expose()
  invoiceAccountLookupId?: string

  @Expose()
  dueDttm?: number;

  @Expose()
  fromDttm?: number;

  @Expose()
  toDttm?: number;

  @Expose()
  tliFromDttm?: number;

  @Expose()
  tliToDttm?: number;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  coded?: boolean

  @Expose()
  disputed?: boolean

  @Expose()
  dbName?: string

  @Expose({name: "lineItemList"})
  @Type(() => TransportLineItemEntity)
  tlis?: TransportLineItemEntity[]

  @Expose({name: "ticketCodeList"})
  @Type(() => TicketCodeEntity)
  codes?: TicketCodeEntity[]

  readonly entityType = 'INVOICE';

  get displayName(): Readonly<string> {
    return this.invoiceNumber;
  }

  /**
   * Constructor for InvoiceEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: Invoice): InvoiceEntity {
    return plainToInstance(InvoiceEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<Invoice> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(InvoiceValidator, object);
  }

  asTypeValue(): Invoice {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(InvoiceValidator, object);
  }
}
